import { Component, OnInit } from '@angular/core'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy } from '@ngneat/until-destroy'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { combineLatest, Observable } from 'rxjs'
import {
  filter,
  first,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Booking } from 'src/app/features/booking/booking'
import { bookingSelectors } from 'src/app/features/booking/booking.selectors'
import { Member } from 'src/app/features/member/member'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { Product } from 'src/app/features/product/product'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { Competition } from '../../competition'
import { CompetitionMatch } from '../../competition-match/competition-match'
import { competitionMatchActions } from '../../competition-match/competition-match.actions'
import { competitionMatchSelectors } from '../../competition-match/competition-match.selectors'
import { CompetitionPool } from '../../competition-pool/competition-pool'
import { competitionPoolActions } from '../../competition-pool/competition-pool.actions'
import { competitionPoolSelectors } from '../../competition-pool/competition-pool.selectors'
import { competitionSelectors } from '../../competition.selectors'
import { CompetitionTeam } from '../competition-team'
import { competitionTeamSelectors } from '../competition-team.selectors'
import { ActivatedRoute } from '@angular/router'
@UntilDestroy()
@Component({
  selector: 'referee-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CompetitionTeamRefereeTabComponent implements OnInit {
  public isLoading$: Observable<boolean>
  public matches$: Observable<CompetitionMatch[]>
  public selectedLanguage$: Observable<string>
  public competitionTeamEntities$: Observable<Dictionary<CompetitionTeam>>
  public bookingEntities$: Observable<Dictionary<Booking>>
  public teamId$: Observable<number>
  public teamId: number
  public team$: Observable<CompetitionTeam>
  public productEntities$: Observable<Dictionary<Product>>
  public competitionPool$: Observable<CompetitionPool>
  public teamIdsOfTheCompetitionPool: number[] = []
  public memberEntities$: Observable<Dictionary<Member>>
  public competition$: Observable<Competition>
  public team: CompetitionTeam
  private isCompetitionPoolFetched: boolean = false

  constructor(private store: Store<AppState>, private route: ActivatedRoute) {}

  ngOnInit() {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.isLoading$ = combineLatest([
      this.store.select(competitionMatchSelectors.selectIsLoading),
      this.store.select(bookingSelectors.selectIsLoading)
    ]).pipe(
      map(([isLoadingCompetitionMatch, isLoadingBooking]) =>
        Boolean(isLoadingCompetitionMatch || isLoadingBooking)
      )
    )

    this.teamId$ = this.route.paramMap.pipe(
      map((params) => params.get('teamId')),
      map((id) => Number(id)),
      filter((id): id is number => !isNaN(id))
    )

    this.teamId$.pipe(first()).subscribe((teamId) => {
      this.teamId = teamId
      this.store.dispatch(
        competitionMatchActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              memberId: `-${teamId}`,
              'booking.startDate': {
                [CondOperator.GREATER_THAN]: moment().toISOString()
              }
            },
            join: [
              { field: 'booking' },
              { field: 'booking.product' },
              { field: 'booking.product.translations' },
              { field: 'team1' },
              { field: 'team2' }
            ]
          })
        })
      )
    })

    this.bookingEntities$ = this.store.select(bookingSelectors.selectEntities)

    this.productEntities$ = this.store.select(productSelectors.selectEntities)

    this.memberEntities$ = this.store.select(memberSelectors.selectEntities)
    this.competitionTeamEntities$ = this.store.select(
      competitionTeamSelectors.selectEntities
    )

    this.matches$ = combineLatest([
      this.store.select(competitionMatchSelectors.selectAll),
      this.bookingEntities$
    ]).pipe(
      map(([matches, bookingEntities]) => {
        return matches
          .filter(
            (match) =>
              match.memberId == -this.teamId &&
              moment(bookingEntities[match?.bookingId]?.startDate).isAfter(
                moment()
              )
          )
          .sort(
            (a, b) =>
              moment(bookingEntities[a.bookingId]?.startDate).valueOf() -
              moment(bookingEntities[b.bookingId]?.startDate).valueOf()
          )
      })
    )
  }

  public isSameBookingDay$(index): Observable<boolean> {
    return this.matches$.pipe(
      take(1),
      withLatestFrom(this.bookingEntities$),
      map(([matches, bookingEntities]) => {
        if (index == 0) {
          return false
        }

        return moment(bookingEntities[matches[index]?.bookingId]?.startDate)
          .startOf('day')
          .isSame(
            moment(
              bookingEntities[matches[index - 1]?.bookingId]?.startDate
            ).startOf('day')
          )
      })
    )
  }
}
